import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';

// A typeahead picker that replaces the native <datalist>. The native control
// filters its suggestions down to whatever text is already in the box, so once
// a full value like "Client 1" is selected it's the only option left and the
// user has to clear the field to switch (UAT, John). This keeps free-text entry
// (trusts, an individual not in the list) but always offers the full list when
// the current value is empty or an exact match, and filters only while the user
// is mid-typing a partial.
//
// The menu renders through a portal with fixed positioning: the table wrapper
// is overflow-x:auto, which would otherwise clip an in-flow dropdown.
export default function Combobox({
  value, onChange, options = [], readOnly = false, ariaLabel,
  // A datalist takes free text, so it needs the same Salesforce length
  // guard as a plain input: maxLength stops new typing, invalid flags a
  // value that was already too long when it arrived.
  maxLength, invalid = false,
}) {
  const v = value ?? '';
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();

  const q = v.trim().toLowerCase();
  const exact = options.some(o => o.toLowerCase() === q);
  // Full list when empty or an exact match; substring filter while typing.
  const filtered = q && !exact
    ? options.filter(o => o.toLowerCase().includes(q))
    : options;

  const reposition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom, left: r.left, width: r.width });
  }, []);

  const openMenu = useCallback(() => {
    if (readOnly || options.length === 0) return;
    reposition();
    setOpen(true);
  }, [readOnly, options.length, reposition]);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIdx(-1);
  }, []);

  const choose = useCallback((opt, refocus = true) => {
    onChange(opt);
    close();
    if (refocus) inputRef.current?.focus();
  }, [onChange, close]);

  // Reposition while open (table can scroll horizontally; page can scroll).
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, reposition]);

  // Close on an interaction outside both the input and the portal menu.
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = e => {
      if (inputRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open, close]);

  const onKeyDown = e => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { openMenu(); return; }
      setActiveIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIdx >= 0 && filtered[activeIdx] != null) {
        // Commit the highlighted option and keep the event from also triggering
        // the table's Enter-moves-down handler.
        e.preventDefault();
        e.stopPropagation();
        choose(filtered[activeIdx]);
      }
      // Otherwise let Enter bubble so the table advances to the next row.
    } else if (e.key === 'Tab') {
      // Tab commits the highlighted match (type "j", Tab -> "Joint") without
      // preventing Tab's native move to the next cell.
      if (open && activeIdx >= 0 && filtered[activeIdx] != null && filtered[activeIdx] !== v) {
        choose(filtered[activeIdx], false);
      }
    } else if (e.key === 'Escape') {
      // First Esc closes the menu (and stops here); with the menu already
      // closed, Esc bubbles to the table which reverts the cell.
      if (open) { e.preventDefault(); e.stopPropagation(); close(); }
    }
  };

  if (readOnly) {
    return <input type="text" value={v} readOnly aria-label={ariaLabel} />;
  }

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        maxLength={maxLength}
        className={invalid ? 'date-invalid' : undefined}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && activeIdx >= 0 ? `${listId}-opt-${activeIdx}` : undefined}
        aria-autocomplete="list"
        value={v}
        onChange={e => {
          onChange(e.target.value);
          // Highlight the first match so Enter/Tab can commit it immediately.
          setActiveIdx(e.target.value.trim() ? 0 : -1);
          openMenu();
        }}
        onFocus={openMenu}
        onClick={openMenu}
        onKeyDown={onKeyDown}
        onBlur={close}
      />
      {open && filtered.length > 0 && rect && createPortal(
        <ul
          ref={menuRef}
          id={listId}
          role="listbox"
          className="combobox-menu"
          style={{ top: rect.top, left: rect.left, minWidth: Math.max(rect.width, 150) }}
        >
          {filtered.map((opt, i) => (
            <li
              key={opt}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={opt.toLowerCase() === q}
              className={'combobox-option' + (i === activeIdx ? ' is-active' : '')}
              // mousedown (not click) so selection fires before the input blur.
              onMouseDown={e => { e.preventDefault(); choose(opt); }}
              onMouseEnter={() => setActiveIdx(i)}
            >
              {opt}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </>
  );
}
